/**
 * V108-8-16 (owner decision 2026-10-18) — a program's own next setup step on its Today card (SOC 2, ISO 42001,
 * GovRAMP, FedRAMP). The step is the program's top open step from GET /enablers (`nextStepFor`: the same ranked step
 * Setup and the coverage view name), with its state when it is already moving. A QUIET link: the page's one filled
 * primary stays wherever `todayPrimary` puts it (usually the shared Next step strip). Nothing when the program has no
 * open step that unlocks one of its objectives.
 */
import { Link } from 'react-router-dom';
import { nextStepFor, nextStepLabel } from '../../lib/setup-model';
import type { EnablerItem } from '../../lib/setup-api';
import { programShort } from '../setup/parts';

/**
 * The step a program card names: its top step that unlocks one of its objectives, else (V108-8-17, flow review F-5) a
 * system no program counts that can join this program ("Add Claims triage to GovRAMP").
 */
export function programSetupStepOf(program: string, enablers: readonly EnablerItem[]): { step: EnablerItem; label: string } | null {
  const step = nextStepFor(program, enablers);
  if (step) return { step, label: nextStepLabel(step) };
  const scope = enablers.find((e) => e.code === 'scope:system' && e.state !== 'done' && e.state !== 'declined' && e.state !== 'snoozed'
    && e.progress.gap?.kind === 'system_scope' && e.progress.gap.programs.some((p) => p.program === program));
  if (scope?.progress.gap?.kind !== 'system_scope') return null;
  const g = scope.progress.gap;
  return { step: scope, label: `Add ${g.name} to ${g.programs.find((p) => p.program === program)!.label}` };
}

export function ProgramSetupStep({ program, enablers, prefix }: { program: string; enablers: readonly EnablerItem[]; prefix: string }) {
  const found = programSetupStepOf(program, enablers);
  if (!found) return null;
  const { step, label } = found;
  return (
    <p className="gp-setup-step" data-part="program-setup-step">
      <span className="gp-setup-step-k">Setup</span>
      <Link to={`${prefix}/setup?step=${encodeURIComponent(step.id)}#steps`} aria-label={`${programShort(program)} setup: ${label}`}>{label}</Link>
    </p>
  );
}
